import { useEffect, useRef, type RefObject } from "react";

/** Give a newly rendered plan a beat to lay out before scrolling to it. */
const SCROLL_DELAY_MS = 100;

interface TodoPlan {
  steps?: unknown[];
}

export interface ChatScroll {
  messagesContainerRef: RefObject<HTMLDivElement | null>;
  bottomRef: RefObject<HTMLDivElement | null>;
  scrollToBottom: (smooth?: boolean) => void;
}

/**
 * Keeps the transcript pinned to the bottom as messages, streaming text and the
 * turn plan change.
 */
export function useChatScroll(
  chatMessages: unknown,
  currentTurnTodoPlan: TodoPlan | null,
  streamingMessages: unknown
): ChatScroll {
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll the messages container to the bottom without triggering iOS viewport scroll.
  // scrollIntoView() on iOS WebKit can scroll all ancestors including the viewport,
  // so we use scrollTop directly on the container instead.
  const scrollToBottom = (smooth = true): void => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (smooth) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Phase 8: Auto-scroll with streaming support
  // Use requestAnimationFrame to batch scroll updates during streaming animation
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      scrollToBottom();
    });
    return () => cancelAnimationFrame(rafId);
    // Re-run whenever messages, research plan, or streaming text changes
  }, [chatMessages, currentTurnTodoPlan, streamingMessages]);

  // Auto-scroll when the research plan is shown or updated
  useEffect(() => {
    if ((currentTurnTodoPlan?.steps?.length ?? 0) > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, SCROLL_DELAY_MS);
    }
  }, [currentTurnTodoPlan]);

  return { messagesContainerRef, bottomRef, scrollToBottom };
}
